/**
 * Price-alerts panel — Phase 6a-2 commit 6 of the decomposition
 * (briefs/ARCHITECTURE_DECOMPOSITION_BLUEPRINT.md §6, A35, v2.5/R21).
 * Body moved VERBATIM from static/index.html's parse-time IIFE; the only
 * edits are the approved ctx.* substitutions.
 *
 * Server-side alerts (engine/price_alerts.py): the backend evaluates the
 * crossing in its own tick pipeline and pushes to Telegram — set one here,
 * close the laptop, still get the ping. This panel is just CRUD over
 * /api/alerts. The UI is injected by this module (button into #top-bar,
 * panel onto document.body) so the whole feature stays in one place.
 *
 * MUST be invoked at the page's original parse-time position: the A35→A38
 * order is load-bearing — initTopBarLayout relocates the injected button by
 * id (put(row,'alerts-btn')), so the button must exist before it runs.
 *
 * The injected context is FROZEN at four entries (v2.5/R21) — additions only
 * via an evidenced blueprint amendment.
 */

export interface AlertsPanelContext {
  /** Page-owned auth header builder (A5). */
  authHeaders(extra?: Record<string, string>): Record<string, string>;
  /** Page-owned 401/banner path — called on both failure paths (R21). */
  handleAuthFailure(resp: { status: number }): void;
  /** Bridged read — resolves page-side at call time (H1). */
  getSymbol(): string;
  /** Plain Hot-rule page field — still page state (rule 2). */
  getLtp(): number | null;
}

export function initAlertsPanel(ctx: AlertsPanelContext): void {
  const topBar = document.getElementById('top-bar');
  if (!topBar) return;

  const btn = document.createElement('button');
  btn.id = 'alerts-btn';
  btn.textContent = '🔔';
  btn.title = 'Price alerts (server-side, Telegram push)';
  topBar.appendChild(btn); // styled by the top-bar design system CSS


  const panel = document.createElement('div');
  panel.id = 'alerts-panel';
  panel.style.cssText = 'display:none;position:fixed;top:44px;right:12px;width:340px;max-height:70vh;'
    + 'overflow-y:auto;z-index:9999;background:#171A1E;color:#E7EBF0;border:1px solid #2b323b;'
    + 'border-radius:8px;padding:12px;font:12px -apple-system,sans-serif;box-shadow:0 6px 24px rgba(0,0,0,0.5);';
  panel.innerHTML =
    '<div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:8px;">' +
    '<b>Price Alerts</b><span id="alerts-close" style="cursor:pointer;padding:0 4px;">✕</span></div>' +
    '<div style="display:grid;grid-template-columns:1fr 1fr;gap:6px;margin-bottom:6px;">' +
    '<input id="alert-symbol" placeholder="Symbol" style="grid-column:1/3;padding:5px;background:#141A22;border:1px solid #2b323b;border-radius:4px;color:inherit;">' +
    '<select id="alert-condition" style="padding:5px;background:#141A22;border:1px solid #2b323b;border-radius:4px;color:inherit;">' +
    '<option value="above">Crosses above</option><option value="below">Crosses below</option></select>' +
    '<input id="alert-price" type="number" step="0.05" placeholder="Price" style="padding:5px;background:#141A22;border:1px solid #2b323b;border-radius:4px;color:inherit;">' +
    '<input id="alert-note" placeholder="Note (optional)" style="grid-column:1/3;padding:5px;background:#141A22;border:1px solid #2b323b;border-radius:4px;color:inherit;">' +
    '<button id="alert-create" style="grid-column:1/3;padding:6px;background:#2962ff;border:none;border-radius:4px;color:#fff;cursor:pointer;">Create alert</button>' +
    '</div><div id="alerts-status" style="min-height:14px;color:#f2a33c;margin-bottom:6px;"></div>' +
    '<div id="alerts-list"></div>';
  document.body.appendChild(panel);

  const setStatus = (msg: string, ok?: boolean) => {
    const el = panel.querySelector('#alerts-status') as HTMLElement;
    el.textContent = msg || '';
    el.style.color = ok ? '#2DBE8E' : '#f2a33c';
  };

  async function refreshList(): Promise<void> {
    try {
      const resp = await fetch('/api/alerts', { headers: ctx.authHeaders() });
      if (!resp.ok) { ctx.handleAuthFailure(resp); setStatus('Login required for alerts'); return; }
      const data = await resp.json();
      const list = panel.querySelector('#alerts-list') as HTMLElement;
      const alerts = data.alerts || [];
      if (!alerts.length) { list.innerHTML = '<div style="color:#787b86;">No alerts yet</div>'; return; }
      list.innerHTML = '';
      for (const a of alerts) {
        const row = document.createElement('div');
        row.style.cssText = 'display:flex;justify-content:space-between;align-items:center;gap:6px;'
          + 'padding:6px;border-bottom:1px solid #23282f;' + (a.active ? '' : 'opacity:0.55;');
        const arrow = a.condition === 'above' ? '↑' : '↓';
        const status = a.active ? '<span style="color:#2DBE8E;">armed</span>'
          : '<span style="color:#787b86;">fired' + (a.triggered_price != null ? ' @ ' + a.triggered_price : '') + '</span>';
        const label = document.createElement('span');
        label.style.cssText = 'flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;';
        label.textContent = `${a.symbol.replace('NSE_INDEX|', '').replace('BSE_INDEX|', '')} ${arrow} ${a.price}` + (a.note ? ' — ' + a.note : '');
        const stat = document.createElement('span'); stat.innerHTML = status;
        const del = document.createElement('span');
        del.textContent = '🗑'; del.style.cursor = 'pointer'; del.title = 'Delete';
        del.addEventListener('click', async () => {
          await fetch('/api/alerts/' + a.id, { method: 'DELETE', headers: ctx.authHeaders() });
          refreshList();
        });
        row.appendChild(label); row.appendChild(stat); row.appendChild(del);
        list.appendChild(row);
      }
    } catch (e) { setStatus('Failed to load alerts'); }
  }

  btn.addEventListener('click', () => {
    const open = panel.style.display !== 'none';
    panel.style.display = open ? 'none' : 'block';
    if (!open) {
      (panel.querySelector('#alert-symbol') as HTMLInputElement).value = ctx.getSymbol() || '';
      const ltp = ctx.getLtp();
      if (ltp) (panel.querySelector('#alert-price') as HTMLInputElement).value = String(ltp);
      setStatus('');
      refreshList();
    }
  });
  (panel.querySelector('#alerts-close') as HTMLElement).addEventListener('click', () => { panel.style.display = 'none'; });

  (panel.querySelector('#alert-create') as HTMLElement).addEventListener('click', async () => {
    const body = {
      symbol: (panel.querySelector('#alert-symbol') as HTMLInputElement).value.trim(),
      condition: (panel.querySelector('#alert-condition') as HTMLSelectElement).value,
      price: parseFloat((panel.querySelector('#alert-price') as HTMLInputElement).value),
      note: (panel.querySelector('#alert-note') as HTMLInputElement).value.trim(),
    };
    if (!body.symbol || !body.price || body.price <= 0) { setStatus('Enter a symbol and a valid price'); return; }
    try {
      const resp = await fetch('/api/alerts', {
        method: 'POST', headers: ctx.authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(body),
      });
      if (!resp.ok) {
        ctx.handleAuthFailure(resp);
        const err = await resp.json().catch(() => ({}));
        setStatus(typeof err.detail === 'string' ? err.detail : 'Failed to create alert');
        return;
      }
      setStatus('Alert armed — Telegram will ping when it crosses', true);
      (panel.querySelector('#alert-note') as HTMLInputElement).value = '';
      refreshList();
    } catch (e) { setStatus('Failed to create alert'); }
  });
}
